/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-pascal-case */
import MaterialReactTable, {
  MaterialReactTableProps,
  MRT_DensityState,
  MRT_FullScreenToggleButton,
} from 'material-react-table';
import { useMediaQuery } from '@mui/material';
import React, { useMemo } from 'react';
import * as R from 'ramda';

import { colors, styled, SystemStyleObject } from 'core/styles';

import { MRT_ToggleRowActionMenuButton } from './components/MRT_ToggleRowActionMenuButton';
import {
  between,
  greaterThan,
  lessThan,
  greaterThanOrEqualTo,
  lessThanOrEqualTo,
} from './filterFns';
import { basic, compareDiffs } from './sortingFns';

export type TableProps<TData extends Record<string, any>> = MaterialReactTableProps<TData> & {
  autoPinningColumns?: string[];
  withFullScreenButton?: boolean;
};

const headCellAlignStyles = (): SystemStyleObject => ({
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'center',
  },
});

export function BaseTable<TData extends Record<string, any> = Record<string, unknown>>({
  autoPinningColumns,
  withFullScreenButton,
  ...props
}: TableProps<TData>) {
  const isDesktop = useMediaQuery('(min-width: 600px)');

  const columnPinning = useMemo(
    () =>
      isDesktop && autoPinningColumns
        ? {
            left: autoPinningColumns,
          }
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [R.toString(autoPinningColumns), isDesktop],
  );

  const localProps: Omit<MaterialReactTableProps<TData>, 'columns' | 'data'> = useMemo(
    () => ({
      state: { columnPinning },
      filterFns: {
        between,
        lessThan,
        lessThanOrEqualTo,
        greaterThan,
        greaterThanOrEqualTo,
      },
      sortingFns: {
        basic,
        diffs: compareDiffs,
      },
      displayColumnDefOptions: {
        'mrt-row-actions': props.renderRowActionMenuItems
          ? {
              Cell: ({ cell, row, table }) => (
                <MRT_ToggleRowActionMenuButton
                  cell={cell as any}
                  row={row as any}
                  table={table as any}
                />
              ),
              muiTableHeadCellProps: {
                sx: headCellAlignStyles,
              },
            }
          : {
              muiTableHeadCellProps: {
                sx: headCellAlignStyles,
              },
            },
        'mrt-row-expand': {
          size: 65,
        },
      },
      muiTablePaperProps: {
        sx: {
          width: '100%',
          overflow: 'hidden',
          boxShadow: 'none',
          position: 'relative',
          background: colors.smokyWhite,
          borderRadius: '16px',

          '.MuiModal-root &': {
            borderRadius: 0,
          },
        },
      },
      muiTableHeadRowProps: { sx: { boxShadow: 'none' } },
      muiTableHeadCellProps: ({ table }) => ({
        sx: {
          paddingBottom: getDensityPadding(table.getState().density),
        },
      }),
    }),
    [columnPinning, props.renderRowActionMenuItems],
  );

  const columns = useMemo(
    () =>
      props.columns.map(column => {
        return column.sortingFn ? column : { ...column, sortingFn: 'basic' };
      }),
    [props.columns],
  );

  return (
    <MaterialReactTable
      {...(R.mergeDeepLeft(props, localProps) as MaterialReactTableProps<TData>)}
      columns={columns}
      muiTableHeadCellProps={({ table }) => ({
        sx: {
          paddingBottom: getDensityPadding(table.getState().density),
        },
      })}
      renderBottomToolbarCustomActions={
        withFullScreenButton
          ? ({ table }) => {
              return (
                <FullScreenButtonBox>
                  <MRT_FullScreenToggleButton table={table} />
                </FullScreenButtonBox>
              );
            }
          : undefined
      }
    />
  );
}

const getDensityPadding = (density: MRT_DensityState) => {
  if (density === 'compact') {
    return '0.25rem';
  }
  if (density === 'comfortable') {
    return '0.75rem';
  }
  if (density === 'spacious') {
    return '1.25rem';
  }
  return '0px';
};

declare module '@tanstack/table-core' {
  interface TableState {
    displaySettings?: {
      fallbackedTotalUSDVariant?: 'value' | 'fallback';
    };
  }
}

const FullScreenButtonBox = styled('div')({
  marginRight: 'auto',
});
